/**
 * TodaysCelebrationsWidget (v37 — May 21, 2026)
 *
 * Dashboard card showing today's birthdays, wedding anniversaries, and work anniversaries
 * for the user's hospital. Hidden completely when there's no one to celebrate (zero clutter).
 */
import { useEffect, useMemo, useState } from 'react';
import { v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Cake, Heart, Briefcase, PartyPopper, Sparkles, Send, Settings as Gear } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const TodaysCelebrationsWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const isHrOrAdmin = useMemo(() => {
    if (user?.is_super_admin) return true;
    const code = (user?.role_code || '').toLowerCase();
    return ['hospital_admin', 'hr', 'it_admin'].includes(code);
  }, [user]);

  useEffect(() => {
    if (!user || !user.hospital_id) return;
    let mounted = true;
    v37API.celebrationsToday()
      .then(res => { if (mounted) setData(res.data); })
      .catch(() => { /* silent — widget just won't render */ });
    return () => { mounted = false; };
  }, [user?.hospital_id]);

  const sendNow = async () => {
    if (!window.confirm("Send today's greeting emails to all celebrants now?")) return;
    setSending(true);
    try {
      const res = await v37API.sendCelebrationsNow();
      const r = res.data;
      toast.success(`${r.sent} greeting${r.sent !== 1 ? 's' : ''} sent · ${r.failed} failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  if (!data || (data.total || 0) === 0) return null;

  const sections = [
    { key: 'birthdays', label: 'Birthday', icon: Cake, color: 'pink', items: data.birthdays || [], extra: (p) => p.age ? `Turning ${p.age}` : null },
    { key: 'wedding_anniversaries', label: 'Wedding Anniversary', icon: Heart, color: 'rose', items: data.wedding_anniversaries || [], extra: (p) => p.years ? `${p.years} years` : null },
    { key: 'work_anniversaries', label: 'Work Anniversary', icon: Briefcase, color: 'blue', items: data.work_anniversaries || [], extra: (p) => p.years ? `${p.years} year${p.years > 1 ? 's' : ''} at the hospital` : null },
  ].filter(s => s.items.length > 0);

  return (
    <div className="bg-gradient-to-br from-pink-50 via-fuchsia-50 to-purple-50 dark:from-pink-900/15 dark:via-fuchsia-900/15 dark:to-purple-900/15 rounded-2xl border border-pink-200 dark:border-pink-700/40 overflow-hidden shadow-sm" data-testid="celebrations-widget">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-pink-200/60 dark:border-pink-700/30 bg-white/60 dark:bg-slate-900/40">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-pink-500 to-fuchsia-600 flex items-center justify-center text-white shadow-sm">
          <PartyPopper size={16} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
            Today&apos;s Celebrations <Sparkles size={12} className="text-fuchsia-500" />
          </h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            {data.total} colleague{data.total > 1 ? 's' : ''} celebrating · {new Date(data.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}
          </p>
        </div>
        {isHrOrAdmin && (
          <div className="flex items-center gap-1.5">
            <button onClick={sendNow} disabled={sending}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-md transition disabled:opacity-50"
              data-testid="celeb-widget-send-now">
              <Send size={11} /> {sending ? 'Sending…' : 'Send Now'}
            </button>
            <button onClick={() => navigate('/celebrations-settings')}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition"
              title="Configure" data-testid="celeb-widget-settings">
              <Gear size={11} />
            </button>
          </div>
        )}
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sections.map(section => {
          const Icon = section.icon;
          const colorClasses = {
            pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-700/40',
            rose: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40',
            blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700/40',
          }[section.color];

          return (
            <div key={section.key} className="bg-white/70 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-800 p-3" data-testid={`celebrations-section-${section.key}`}>
              <div className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border mb-2', colorClasses)}>
                <Icon size={10} /> {section.label}
              </div>
              <ul className="space-y-2">
                {section.items.map(p => (
                  <li key={`${section.key}-${p.user_id}`} className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300 shrink-0">
                      {(p.name || '?').split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                        {p.designation || p.department}
                        {section.extra(p) && <span className="text-fuchsia-600 dark:text-fuchsia-400 ml-1">· {section.extra(p)}</span>}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TodaysCelebrationsWidget;
